import React from 'react';
import { useLocation } from 'react-router-dom';

export interface SidebarGroupProps {
  title: string;
  storageKey: string;
  activePaths: string[];
  hasBadge?: boolean;
  hidden?: boolean;
  children: React.ReactNode;
}

const readStored = (key: string): boolean | null => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === '1') return true;
    if (raw === '0') return false;
    return null;
  } catch {
    return null;
  }
};

const writeStored = (key: string, open: boolean) => {
  try {
    window.localStorage.setItem(key, open ? '1' : '0');
  } catch {
    /* localStorage unavailable — ignore */
  }
};

const matchesAnyPath = (pathname: string, activePaths: string[]): boolean =>
  activePaths.some((p) => pathname === p || pathname.startsWith(p + '/'));

const SidebarGroup: React.FC<SidebarGroupProps> = ({
  title,
  storageKey,
  activePaths,
  hasBadge = false,
  hidden = false,
  children,
}) => {
  const { pathname } = useLocation();

  const [open, setOpen] = React.useState<boolean>(() => {
    const stored = readStored(storageKey);
    if (stored !== null) return stored;
    return matchesAnyPath(pathname, activePaths);
  });

  if (hidden) {
    return <>{children}</>;
  }

  const toggle = () => {
    const next = !open;
    setOpen(next);
    writeStored(storageKey, next);
  };

  return (
    <div className="sidebar__group">
      <button
        type="button"
        className="sidebar__group-header"
        aria-expanded={open}
        onClick={toggle}
      >
        <span className="sidebar__group-title">{title}</span>
        {hasBadge && !open && (
          <span className="sidebar__group-badge-dot" data-testid="sidebar-group-badge-dot" />
        )}
        <span className={`sidebar__group-chevron${open ? ' open' : ''}`} aria-hidden="true">
          ›
        </span>
      </button>
      {open && <div className="sidebar__group-items">{children}</div>}
    </div>
  );
};

export default SidebarGroup;
